import { TZDate } from '@date-fns/tz';
import { IconButton, InputAdornment } from '@mui/material';
import { Icon } from '@shared/icons';
import { userTimeZone } from '@shared/utils/date';
import { isBetween } from '@shared/utils/number';
import type { Column, FilterFn, Row } from '@tanstack/react-table';
import { subDays } from 'date-fns';
import {
  DateRangePicker,
  type DateRangePickerTriggerProps,
  useBuiltInPresets,
} from '../../DateRange';
import { TextField } from '../../Inputs';
import { useFilterLabel } from '../hooks/useFilterLabel';
import type { ColumnFilterProps } from '../types';
import { useTableTexts } from '../useTableTexts';
import { ColumnFilterProvider, useColumnFilter } from './ColumnFilterContext';
import { getFilterInputProps } from './util';

function getTimeRange(column: Column<any, any>) {
  const filter = column.getFilterValue() as (number | null)[];

  if (
    Array.isArray(filter) &&
    typeof filter[0] === 'number' &&
    typeof filter[1] === 'number'
  ) {
    return { from: new Date(filter[0]), to: new Date(filter[1]) };
  }
  const tz = column.columnDef.meta?.timeZone ?? userTimeZone();
  const now = new TZDate(new Date(), tz);
  return { from: subDays(now, 7), to: now };
}

export const DateRangeColumnFilter = ({
  column,
  layout,
}: ColumnFilterProps) => {
  const { from, to } = getTimeRange(column);
  const timeZone = column.columnDef.meta?.timeZone;
  const presets = Object.values(useBuiltInPresets());

  return (
    <ColumnFilterProvider column={column} layout={layout}>
      <DateRangePicker
        onChange={(value) => {
          column.setFilterValue([value.from.getTime(), value.to.getTime()]);
        }}
        presets={presets}
        slots={{ trigger: Trigger }}
        timeZone={timeZone}
        value={{ from, to }}
      />
    </ColumnFilterProvider>
  );
};

const Trigger = ({ onClick }: DateRangePickerTriggerProps) => {
  const { column, layout } = useColumnFilter();
  const texts = useTableTexts();
  const [from, to] = (column?.getFilterValue() as (number | null)[]) ?? [];
  const label = useFilterLabel(column) ?? '';

  const onClear = () => {
    column?.setFilterValue(undefined);
  };

  return (
    <TextField
      value={label}
      onClick={onClick}
      slotProps={{
        input: {
          endAdornment: (from || to) && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
              >
                <Icon name="close" size="small" />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
      {...getFilterInputProps(column, layout, texts)}
    />
  );
};

function getColumnValue(row: Row<any, any>, columnId: string) {
  const value = row.getValue(columnId);
  if (value instanceof Date) {
    return value;
  }
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }
  return new Date(value);
}

export const dateRangeFilterFn: FilterFn<any, any> = (
  row: Row<any, any>,
  columnId: string,
  filterValue: unknown[] = []
) => {
  const [from, to] = filterValue;
  const value = getColumnValue(row, columnId);

  if (typeof from !== 'number' || typeof to !== 'number' || value === null) {
    return false;
  }

  return isBetween({
    value: value.getTime(),
    from,
    to,
  });
};
